import React, { useMemo, useState } from 'react'

import FAQ from 'components/FAQ/FAQ'
import ToggleButton from 'components/Button/ToggleButton'
import ComingSoon from 'components/ComingSoon/ComingSoon'

// import ArrowRightIcon from 'assets/icons/ArrowRightIcon'
// import CheckCircleIcon from 'assets/icons/CheckCircleIcon'
import KeyboardArrowDownIcon from 'assets/icons/KeyboardArrowDownIcon'

import { products } from 'utils/constants/product'
import { addOns, reviews } from 'utils/constants/pricing'
import CheckIcon from 'assets/icons/CheckIcon'
import { useRequestDemoModal } from 'context/RequestDemoModalContext'
import ContactWhatsapp from 'components/ContactWhatsapp/ContactWhatsapp'
import Tooltip from 'components/Tooltip/Tooltip'

// Fixed locale so prices render the same on the server, at build time and in every browser.
const formatNumber = (value) => new Intl.NumberFormat('id-ID').format(value || 0)

// Plans whose features carry a `group` are shown grouped. On the plan cards only the
// first CARD_GROUP_LIMIT groups are listed in full, the rest link to the compare table.
const CARD_DETAILS_GROUP = "Plan details"
const CARD_GROUP_LIMIT = 2

const isGrouped = (features) => features?.some(feature => feature?.group)

// [{ group, features: [...] }] in order of first appearance
const groupFeatures = (features = []) => {
    const groups = []
    features.forEach((feature) => {
        const last = groups[groups.length - 1]
        if (last && last.group === feature.group) {
            last.features.push(feature)
        } else {
            groups.push({ group: feature.group, features: [feature] })
        }
    })
    return groups
}

function PlanFeatures({ pricing }) {
    if (!isGrouped(pricing?.features)) {
        return (
            <ul className='flex flex-col gap-2'>
                {pricing?.features?.map((feature, i) => (
                    <li key={i} className='flex items-center gap-2 mb-2'>
                        <div>
                            <CheckIcon />
                        </div>
                        <p className='text-xs lg:text-sm text-maplexxon-grey-2'>{feature.text}{feature.value ? ` (${feature.value})` : ""}</p>
                    </li>
                ))}
            </ul>
        )
    }

    const groups = groupFeatures(pricing.features)
    const details = groups.find(g => g.group === CARD_DETAILS_GROUP)
    const included = groups.filter(g => g.group !== CARD_DETAILS_GROUP)
    const shown = included.slice(0, CARD_GROUP_LIMIT)
    const hidden = included.slice(CARD_GROUP_LIMIT)

    return (
        <div>
            {details && (
                <dl className='mb-6'>
                    {details.features.map((feature, i) => (
                        <div key={i} className='flex justify-between gap-4 py-2 border-b border-maplexxon-grey-light-2 text-xs lg:text-sm'>
                            <dt className='text-maplexxon-grey-2'>{feature.text}</dt>
                            <dd className={`text-right font-medium ${feature.value?.length <= 16 ? "whitespace-nowrap" : ""} ${feature.excluded ? "text-maplexxon-grey-4" : "text-black"}`}>{feature.value}</dd>
                        </div>
                    ))}
                </dl>
            )}
            <p className='text-xs lg:text-sm font-bold uppercase text-maplexxon-grey-2 mb-2'>Every plan includes</p>
            {shown.map((g, i) => (
                <div key={i} className='mb-4'>
                    <p className='text-xs lg:text-sm font-semibold text-maplexxon-blue mb-2'>{g.group}</p>
                    <ul className='flex flex-col gap-2'>
                        {g.features.map((feature, j) => (
                            <li key={j} className='flex items-start gap-2'>
                                <div className='pt-1'>
                                    <CheckIcon />
                                </div>
                                <p className='text-xs lg:text-sm text-maplexxon-grey-2'>{feature.text}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
            {hidden.length > 0 && (
                <div>
                    <p className='text-xs lg:text-sm text-maplexxon-grey-2 mb-2'>
                        Also included: {hidden.map(g => g.group.toLowerCase()).join(", ")}.
                    </p>
                    <a href='#compare-plans' className='text-xs lg:text-sm font-medium text-maplexxon-blue underline'>See all features</a>
                </div>
            )}
        </div>
    )
}

function Pricing() {
    const { setRequestDemoModalOpen } = useRequestDemoModal();

    const selectableProducts = products.filter(product => !product.beta)

    const [annually, setAnnually] = useState(false)
    const [selectedProductId, setSelectedProductId] = useState(selectableProducts[0]?.id)

    const [addOnsOpen, setAddOnsOpen] = useState([])

    const selectedProduct = products.find(product => product.id === selectedProductId)
    const comingSoon = Boolean(selectedProduct?.comingSoon)
    const selectedPricings = useMemo(() => (comingSoon ? [] : (selectedProduct?.pricings || [])), [comingSoon, selectedProduct])
    const grouped = selectedPricings.some(pricing => isGrouped(pricing?.features))

    const handleToggleAddOns = (index) => {
        if (addOnsOpen.includes(index)) {
            setAddOnsOpen(prev => prev.filter(i => i !== index))
        } else {
            setAddOnsOpen(prev => ([...prev, index]))
        }
    }

    const handleSelectProduct = (e) => {
        const id = Number(e?.target?.value)
        if (id) {
            setSelectedProductId(id)
        }
    }

    // Union of the plans' features, in order. Computed during render (not in an effect)
    // so the table is also present when the page is rendered to a string at build time.
    const features = useMemo(() => {
        const union = []
        selectedPricings.forEach((pricing) => {
            pricing.features?.forEach((feature) => {
                if (!union.find(f => f?.text === feature?.text)) {
                    union.push(feature)
                }
            })
        })
        return union
    }, [selectedPricings])

    // Table rows: a group header row whenever the group changes, then the feature row.
    const tableRows = useMemo(() => {
        const rows = []
        let currentGroup
        features.forEach((feature) => {
            if (feature.group && feature.group !== currentGroup) {
                rows.push({ type: 'group', group: feature.group })
            }
            currentGroup = feature.group
            rows.push({ type: 'feature', feature })
        })
        return rows
    }, [features])

    return (
        <div>
            <section className='bg-maplexxon-blue'>
                <div className='flex flex-col items-center max-w-main pt-4 pb-8 lg:py-16'>
                    <p className='text-base lg:text-xl text-center uppercase tracking-widest mb-4 text-white maplexxon-animation-translate-start'>Choose Solutions</p>
                    <div className='relative flex items-center mb-4 md:mb-8 maplexxon-animation-translate-start overflow-clip'>
                        <select
                            className="w-52 sm:w-auto pr-8 truncate appearance-none border border-white bg-maplexxon-blue focus:outline-none text-white rounded-xl py-2 px-3 text-xs lg:text-sm overflow-clip"
                            value={selectedProductId}
                            onChange={handleSelectProduct}
                        >
                            {selectableProducts.map((product) => (
                                <option className="w-min" key={product.id} value={product.id}>{product.name}</option>
                            ))}
                        </select>
                        <KeyboardArrowDownIcon className="absolute right-0 mr-3 w-3 fill-white" />
                    </div>
                    {comingSoon ? (
                        <ComingSoon
                            name={selectedProduct.name}
                            icon={selectedProduct.icon}
                            description={selectedProduct.comingSoonText}
                        />
                    ) : (
                        <>
                            <div className='flex gap-2 items-center w-full mb-4'>
                                <div className='flex gap-2 items-center maplexxon-animation-translate-start'>
                                    <ToggleButton
                                        name="annually"
                                        checked={annually}
                                        onChange={(e) => setAnnually(e.target.checked)}
                                    />
                                    <p className='text-xs lg:text-sm uppercase text-white font-bold'>Annual Plan</p>
                                </div>
                                <Tooltip
                                    text={
                                        <p className='font-semibold text-[10px] sm:text-xs text-black'>
                                            by paying 12 months (annual package) upfront, once a year
                                        </p>
                                    }
                                />
                            </div>
                            <div className={`grid grid-cols-1 ${selectedPricings?.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"} gap-4 md:gap-8`}>
                                {selectedPricings?.map((pricing, i) => {
                                    const groupedPlan = isGrouped(pricing?.features)
                                    return (
                                        <div key={pricing.id ?? i} className={groupedPlan ? "flex flex-col" : ""}>
                                            {pricing?.mostPopular && (
                                                <div className='px-8 sm:-mt-3'>
                                                    <p className=' bg-maplexxon-mint text-white font-bold w-fit ml-auto py-1 px-2 rounded-t-lg uppercase text-xs sm:text-sm'>
                                                        Most popular
                                                    </p>
                                                </div>
                                            )}
                                            <div className={`bg-white p-4 md:py-8 md:px-6 rounded-xl drop-shadow-xl w-full ${groupedPlan ? "flex-1" : ""} ${pricing?.mostPopular ? "ring-4 ring-maplexxon-mint" : "sm:mt-4"}`}>
                                                <p className='text-lg lg:text-2xl font-semibold'>{pricing?.name}</p>
                                                {groupedPlan && (
                                                    <p className='text-xs lg:text-sm mt-1 mb-4 text-maplexxon-grey-2'>{pricing?.description}</p>
                                                )}
                                                {/* <p className='text-xs lg:text-sm mb-4'>{pricing?.description}</p> */}
                                                <div className='text-xs lg:text-sm mb-4 text-maplexxon-grey-2'>
                                                    <p className='text-xs lg:text-sm font-bold uppercase'>Core pricing</p>
                                                    <p className='text-xs lg:text-sm'>Rp {formatNumber(pricing?.corePrice)}</p>
                                                </div>
                                                <p className='text-lg lg:text-xl text-maplexxon-blue mt-4'>
                                                    <span className='font-bold'>Rp {formatNumber(annually ? pricing?.annualPrice : pricing?.monthlyPrice)}&nbsp;</span>
                                                    per {annually ? "year" : 'month'}
                                                </p>
                                                <p className='text-xs lg:text-sm text-maplexxon-grey-2 mb-2'>{pricing.priceDescriptionText}</p>
                                                {annually && (
                                                    <p className='text-xs lg:text-sm text-maplexxon-grey-2'><span className='line-through text-maplexxon-grey-4'>Rp {formatNumber(pricing?.monthlyPrice)}</span>&nbsp;<span className='font-medium'>Rp {formatNumber(pricing?.discountedMonthlyPrice)} per month</span></p>
                                                )}
                                                <div className='text-center mt-6'>
                                                    <button onClick={() => setRequestDemoModalOpen(true)} className='px-4 py-2 text-white rounded-md bg-maplexxon-blue mx-auto mb-10'>Get started</button>
                                                </div>
                                                <PlanFeatures pricing={pricing} />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {!comingSoon && (
                <section id='compare-plans' className='bg-white scroll-mt-16'>
                    <div className='flex flex-col max-w-main pt-4 pb-8 lg:py-16'>
                        <p className='text-lg lg:text-2xl mb-8 font-bold'>Compare plans and features</p>
                        <div className='flex-1 overflow-x-auto mb-8 pb-4'>
                            <table className={`w-full border-spacing-0 border-separate table-auto rounded-xl border border-maplexxon-blue ${grouped ? "min-w-[36rem]" : ""}`}>
                                <thead>
                                    <tr className='rounded-t-xl'>
                                        <th className='bg-maplexxon-grey-5 font-medium rounded-tl-xl border-r border-r-maplexxon-blue text-sm lg:text-lg text-left pt-3 pb-1 px-2 md:pt-6 md:pb-2 md:pl-4'>Features</th>
                                        {selectedPricings.map((pricing, i) => (
                                            <th key={i} className={`bg-maplexxon-grey-5 font-semibold text-maplexxon-blue ${i === selectedPricings.length - 1 ? "rounded-tr-xl" : "border-r border-r-maplexxon-blue"} text-sm lg:text-lg text-left pt-3 pb-1 px-2 md:pt-6 md:pb-2 md:pl-4`}>
                                                {pricing.name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableRows.map((row, index) => {
                                        if (row.type === 'group') {
                                            return (
                                                <tr key={index}>
                                                    <td colSpan={selectedPricings.length + 1} className='bg-maplexxon-blue-light text-xs lg:text-sm font-bold uppercase text-maplexxon-blue py-2 px-2 md:py-3 md:pl-4'>
                                                        {row.group}
                                                    </td>
                                                </tr>
                                            )
                                        }
                                        const item = row.feature
                                        const edge = index === tableRows.length - 1 ? "pb-4 md:pb-8" : index === 0 ? "pt-4 md:pt-8" : ""
                                        const divider = item.group ? "border-b border-b-maplexxon-grey-light-2" : ""
                                        return (
                                            <tr key={index}>
                                                <td className={`text-xs lg:text-sm py-2 px-2 md:py-4 md:pl-4 border-r border-r-maplexxon-blue ${divider} ${edge}`}>{item.text}</td>
                                                {selectedPricings.map((pricing, i) => {
                                                    const feature = pricing?.features?.find(feature => feature?.text === item?.text)
                                                    const included = Boolean(feature) && !feature.excluded
                                                    const value = included ? (feature.value ? feature.value : "Yes") : (item.group ? "Not included" : "Not Included")
                                                    return (
                                                        <td key={i} className={`text-xs lg:text-sm py-2 px-2 md:py-4 md:pl-4 ${item.group ? "min-w-[8.5rem] md:min-w-[11rem]" : ""} ${i === selectedPricings.length - 1 ? "" : "border-r border-r-maplexxon-blue"} ${divider} ${edge} ${item.group && !included ? "text-maplexxon-grey-4" : ""}`}>
                                                            {item.group && included && !feature.value ? (
                                                                <span className='flex items-center gap-2'><CheckIcon /><span>{value}</span></span>
                                                            ) : value}
                                                        </td>
                                                    )
                                                })}
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className='grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12'>
                            {selectedPricings.map((pricing, i) => (
                                <div key={i}>
                                    <div key={i} className='border border-maplexxon-blue bg-maplexxon-grey-3 p-4 md:p-6 rounded-xl mb-2 md:mb-4'>
                                        <p className='text-base lg:text-xl mb-2'>{pricing.name}</p>
                                        <div className='text-xs lg:text-sm mb-4 text-maplexxon-grey-2'>
                                            <p className='text-xs lg:text-sm font-bold uppercase'>Core pricing</p>
                                            <p className='text-xs lg:text-sm'>Rp {formatNumber(pricing?.corePrice)}</p>
                                        </div>
                                        <p className='text-lg lg:text-xl text-maplexxon-blue mt-4'>
                                            <span className='font-bold'>Rp {formatNumber(annually ? pricing?.annualPrice : pricing?.monthlyPrice)}&nbsp;</span>
                                            per {annually ? "year" : 'month'}
                                        </p>
                                    </div>
                                    <button onClick={() => setRequestDemoModalOpen(true)} className='text-base lg:text-xl font-medium px-4 py-2 text-white rounded-md bg-maplexxon-blue mr-auto'>Get started</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className='bg-maplexxon-blue-light'>
                <div className='flex flex-col max-w-main pt-4 pb-8 lg:py-16'>
                    <p className='text-lg lg:text-2xl font-bold mb-2 maplexxon-animation-translate-start'>Recommended Add Ons</p>
                    <p className='text-sm lg:text-lg mb-8 maplexxon-animation-translate-start'>
                        Personalize and enhance your plan with these additional features.
                    </p>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 max-w-3xl'>
                        {addOns.map((item, i) => (
                            <div key={item.id} className='p-2 sm:p-4 border border-maplexxon-blue rounded-xl space-y-2 sm:space-y-4 maplexxon-animation-translate-start'>
                                <div className='flex items-start justify-between gap-2 cursor-pointer select-none' onClick={() => handleToggleAddOns(i)} >
                                    <p className='text-base lg:text-xl font-medium'>{item.name}</p>
                                    <button className={`${addOnsOpen.includes(i) ? "rotate-180" : "rotate-0"} transition-all`} >
                                        <KeyboardArrowDownIcon className="h-6 w-3 fill-maplexxon-grey-2" />
                                    </button>
                                </div>
                                <p className='text-xs lg:text-sm'>{item.description}</p>
                                {addOnsOpen.includes(i) && (
                                    <p className='pt-2 md:pt-4 text-xs lg:text-sm border-t border-maplexxon-grey-2'>
                                        {item.description2}
                                    </p>
                                )}
                                {item.multiPrice ? (
                                    <div>
                                        <p className='text-lg lg:text-xl text-maplexxon-blue'>
                                            Start from
                                        </p>
                                        <div className='flex items-center gap-2 sm:gap-4'>
                                            {item?.price?.map((p, i) => (
                                                <React.Fragment key={i}>
                                                    <div>
                                                        <p className='text-lg lg:text-xl text-maplexxon-blue'>
                                                            <span className='font-bold'>Rp {formatNumber(p)}</span>
                                                        </p>
                                                        <p className='text-xs lg:text-sm text-maplexxon-blue lowercase'>{item.type[i]}</p>
                                                    </div>
                                                    {i !== item.price.length - 1 && (
                                                        <div className='text-sm lg:text-base text-maplexxon-blue'>
                                                            +
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <p className='text-lg lg:text-xl text-maplexxon-blue'>Start from <span className='font-bold'>Rp {formatNumber(item?.price)}</span></p>
                                        <p className='text-xs lg:text-sm text-maplexxon-blue'>{item.type}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* <section section className='bg-maplexxon-grey-light'>
                <div className='flex flex-col max-w-main pt-4 pb-8 lg:py-16'>
                    <div className='md:w-1/2 mb-12 md:mb-24'>
                        <p className='text-maplexxon-purple font-bold text-xl lg:text-3xl mb-2 maplexxon-animation-translate-start'>Used by the world's most amazing business</p>
                        <p className='text-base lg:text-xl maplexxon-animation-translate-start'>Lorem ipsum dolor sit amet consectetur. Vitae lacus quis pharetra faucibus a enim vel risus. </p>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-16 mb-12 md:mb-24'>
                        {reviews.map((review, i) => (
                            <div key={i} className='maplexxon-animation-translate-start'>
                                <p className='text-base lg:text-xl text-maplexxon-grey-2 font-medium mb-2 md:mb-8'>"{review.text}"</p>
                                <p className='text-base lg:text-xl font-medium mb-2'>{review.name}, {review.company}</p>
                                <p className='text-xs lg:text-sm text-maplexxon-grey-4'>{review.role}</p>
                            </div>
                        ))}
                    </div>
                    <div>
                        <button className='flex gap-4 items-center maplexxon-animation-scale-start'>
                            <p className='text-xs lg:text-sm font-bold'>Read all customer reviews</p>
                            <ArrowRightIcon className="w-4 h-4 fill-black" />
                        </button>
                    </div>
                </div>
            </section> */}

            <ContactWhatsapp />
            <FAQ />
        </div>
    )
}

export default Pricing